import { useState } from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useDashboardTheme, cardSx, panelHeaderSx, countBadgeSx, tableHeadRowSx, tableRowSx, scrollbarSx, fmt, fmtBtc, fmtTimeShort } from '../utils';

const statusColors = { in_progress: 'warning', completed: 'success', failed: 'error' };
const winnerStatusColors = { pending: 'warning', credited: 'success', failed: 'error', skipped: 'default' };

function AirdropRow({ dist, index, t }) {
  const [open, setOpen] = useState(false);
  const winners = dist.winners || [];
  const totalPotUsd = winners.reduce((s, w) => s + (Number(w.amountUsd) || 0), 0);

  return (
    <>
      <TableRow sx={tableRowSx(t, { index })}>
        <TableCell>
          <MDBox display='flex' alignItems='center'>
            {winners.length > 0 && (
              <IconButton size='small' onClick={() => setOpen(!open)} sx={{ color: t.tableHeaderColor, p: 0, mr: 0.5 }}>
                <Icon fontSize='small'>{open ? 'expand_less' : 'expand_more'}</Icon>
              </IconButton>
            )}
            <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: t.secondaryText }}>{fmtTimeShort(dist.createdAt)}</MDTypography>
          </MDBox>
        </TableCell>
        <TableCell>
          <Chip label={dist.status.charAt(0).toUpperCase() + dist.status.slice(1).replace('_', ' ')} color={statusColors[dist.status] || 'default'} size='small' sx={{ height: 18, fontSize: '0.63rem' }} />
        </TableCell>
        <TableCell align='right'>
          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{fmtBtc(dist.totalPotBtc)}</MDTypography>
        </TableCell>
        <TableCell align='right'>
          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>${fmt(totalPotUsd)}</MDTypography>
        </TableCell>
        <TableCell align='right'>
          <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: t.primaryText }}>{winners.length}</MDTypography>
        </TableCell>
        <TableCell>
          {dist.apiReported ? (
            <Chip label='Yes' color='success' size='small' sx={{ height: 16, fontSize: '0.6rem' }} />
          ) : (
            <Chip label='No' color='default' size='small' sx={{ height: 16, fontSize: '0.6rem' }} />
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, px: 0, bgcolor: t.isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.03)', borderBottom: `1px solid ${t.rowBorder}` }}>
            <Collapse in={open}>
              <MDBox px={{ xs: 2, sm: 3 }} py={1.5}>
                <MDBox display='flex' gap={1.5} flexWrap='wrap' mb={winners.length > 0 ? 1.5 : 0}>
                  <Chip label={`Engine: ${fmtBtc(dist.enginePotBtc)} BTC`} size='small' variant='outlined' sx={{ fontSize: '0.62rem', height: 20 }} />
                  <Chip label={`Accumulated: ${fmtBtc(dist.accumulatedPotBtc)} BTC`} size='small' variant='outlined' sx={{ fontSize: '0.62rem', height: 20 }} />
                  <Chip label={`API Reported: ${fmtBtc(dist.apiReportedAmountBtc)} BTC`} size='small' variant='outlined' sx={{ fontSize: '0.62rem', height: 20 }} />
                </MDBox>
                {winners.length > 0 && (
                  <Table size='small' sx={{ '& td, & th': { border: 'none', py: 0.5, px: 1, fontSize: '0.7rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: t.tableHeaderColor, fontWeight: 700, fontSize: '0.6rem !important', textTransform: 'uppercase', letterSpacing: '0.06em' }}>User</TableCell>
                        <TableCell align='right' sx={{ color: t.tableHeaderColor, fontWeight: 700, fontSize: '0.6rem !important', textTransform: 'uppercase', letterSpacing: '0.06em' }}>BTC</TableCell>
                        <TableCell align='right' sx={{ color: t.tableHeaderColor, fontWeight: 700, fontSize: '0.6rem !important', textTransform: 'uppercase', letterSpacing: '0.06em' }}>USD</TableCell>
                        <TableCell sx={{ color: t.tableHeaderColor, fontWeight: 700, fontSize: '0.6rem !important', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {winners.map((w, i) => (
                        <TableRow key={i} sx={{ '&:last-child td': { pb: 0 } }}>
                          <TableCell>
                            <MDTypography variant='caption' sx={{ fontSize: '0.7rem', color: t.primaryText }}>{w.name || w.userId}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' sx={{ fontSize: '0.7rem', color: t.primaryText }}>{fmtBtc(w.amountBtc)}</MDTypography>
                          </TableCell>
                          <TableCell align='right'>
                            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.7rem', color: t.primaryText }}>${fmt(w.amountUsd)}</MDTypography>
                          </TableCell>
                          <TableCell>
                            <Chip label={w.status.charAt(0).toUpperCase() + w.status.slice(1)} color={winnerStatusColors[w.status] || 'default'} size='small' sx={{ height: 16, fontSize: '0.58rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </MDBox>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AirdropDistributionHistory({ airdropHistory, loading }) {
  const t = useDashboardTheme();
  const data = airdropHistory?.data || [];
  const failedCount = data.filter((d) => d.status === 'failed').length;

  return (
    <Card sx={cardSx}>
      <MDBox sx={panelHeaderSx(t)}>
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: t.primaryText }}>
          Airdrop Distributions
        </MDTypography>
        <MDBox display='flex' gap={1} alignItems='center'>
          {failedCount > 0 && <Chip label={`${failedCount} failed`} color='error' size='small' sx={{ fontWeight: 600, fontSize: '0.63rem' }} />}
          <MDBox sx={countBadgeSx(t)}>
            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: t.tableHeaderColor, letterSpacing: '0.04em' }}>
              {airdropHistory?.total ?? 0} total
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      {loading && data.length === 0 ? (
        <MDBox sx={{ p: { xs: 2, sm: 3 } }}>
          <Skeleton variant='rectangular' height={200} sx={{ borderRadius: '8px' }} />
        </MDBox>
      ) : data.length === 0 ? (
        <MDBox display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={4} gap={1}>
          <MDTypography variant='body2' color='text' sx={{ opacity: 0.45 }}>No airdrop distributions</MDTypography>
        </MDBox>
      ) : (
        <TableContainer sx={{ ...scrollbarSx(t), maxHeight: 360 }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow sx={tableHeadRowSx(t)}>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right'>Pot (BTC)</TableCell>
                <TableCell align='right'>Pot (USD)</TableCell>
                <TableCell align='right'>Winners</TableCell>
                <TableCell>API</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((dist, index) => (
                <AirdropRow key={dist.id} dist={dist} index={index} t={t} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

export default AirdropDistributionHistory;
